import { getEnvironment } from "utils";

const CROWDIN_JIPT_SCRIPT_ID = "crowdin-jipt";
const CROWDIN_JIPT_SRC = "//cdn.crowdin.com/jipt/jipt.js";
const CROWDIN_PROJECT = "klimtranslations";

const CROWDIN_IN_CONTEXT_FAILURE_TEXT_FRAGMENTS = [
  "Something went wrong while performing request",
  "Could not load data from remote server",
  "Crowdin In-Context was unable to identify translatable texts",
];

type JiptConfigEntry = [string, ...unknown[]];

type JiptWindow = Window & {
  _jipt?: JiptConfigEntry[];
  jipt?: {
    start?: () => void;
    stop?: () => void;
  };
};

export type LoadCrowdinInContextOptions = {
  // Crowdin's escape action: restore the user's pre-Acholi language
  onEscape?: () => void;
  // JIPT failed to load, could not be started, or rendered a known fatal error
  onFailure?: () => void;
};

let failureObserver: MutationObserver | null = null;
let failureExitTriggered = false;
// Crowdin exposes start()/stop() but no readable running state, so track it ourselves
let crowdinInContextRunning = false;

const stopCrowdinInContextFailureWatch = (): void => {
  failureObserver?.disconnect();
  failureObserver = null;
  failureExitTriggered = false;
};

const documentShowsCrowdinFailure = (): boolean => {
  const bodyText = document.body?.textContent ?? "";
  return CROWDIN_IN_CONTEXT_FAILURE_TEXT_FRAGMENTS.some((fragment) => bodyText.includes(fragment));
};

const triggerCrowdinFailureExit = (onFailure?: () => void): void => {
  if (!onFailure || failureExitTriggered) return;
  failureExitTriggered = true;
  stopCrowdinInContextFailureWatch();
  onFailure();
};

export const watchCrowdinInContextFailure = (onFailure: () => void): void => {
  stopCrowdinInContextFailureWatch();

  if (documentShowsCrowdinFailure()) {
    triggerCrowdinFailureExit(onFailure);
    return;
  }

  failureObserver = new MutationObserver(() => {
    if (documentShowsCrowdinFailure()) {
      triggerCrowdinFailureExit(onFailure);
    }
  });
  if (document.body) {
    failureObserver.observe(document.body, { childList: true, subtree: true, characterData: true });
  }
};

// JIPT cannot initialise over local http, so in-context needs a secure non-production context
export const isCrowdinInContextSupported = (): boolean => !getEnvironment().production && window.location.protocol === "https:";

// Kept separate from loading the script so JIPT's scan never lands mid-render
export const startCrowdinInContext = (onFailure?: () => void): void => {
  if (crowdinInContextRunning) return;

  const jiptWindow = window as unknown as JiptWindow;
  // Startup is manual, so without start() in-context would sit dead with no signal
  if (!jiptWindow.jipt?.start) {
    triggerCrowdinFailureExit(onFailure);
    return;
  }

  jiptWindow.jipt.start();
  crowdinInContextRunning = true;
};

// Stops JIPT but leaves the script in place, so it can be started again
export const stopCrowdinInContext = (): void => {
  if (!crowdinInContextRunning) return;
  (window as unknown as JiptWindow).jipt?.stop?.();
  crowdinInContextRunning = false;
};

// Lets the current render cycle finish before JIPT may rewrite the DOM
const startAfterRender = (onFailure?: () => void): void => {
  requestAnimationFrame(() => requestAnimationFrame(() => startCrowdinInContext(onFailure)));
};

// Loads the JIPT editor on demand (Acholi only), idempotently: repeat calls add no script or config.
// React's protection against JIPT's DOM rewrites is not handled here — see reconcilerDomGuard.
export const loadCrowdinInContext = (options?: LoadCrowdinInContextOptions): void => {
  if (!isCrowdinInContextSupported()) return;

  const jiptWindow = window as unknown as JiptWindow;
  if (!jiptWindow._jipt) {
    jiptWindow._jipt = [];
  }
  if (!jiptWindow._jipt.some((entry) => entry[0] === "project")) {
    jiptWindow._jipt.push(["project", CROWDIN_PROJECT]);
  }
  // Crowdin's SPA mode; without it JIPT scans as soon as the script loads
  if (!jiptWindow._jipt.some((entry) => entry[0] === "start_type")) {
    jiptWindow._jipt.push(["start_type", "manual"]);
  }
  // Tags only what is on screen, so a scan rewrites far fewer nodes at once on a long results page.
  if (!jiptWindow._jipt.some((entry) => entry[0] === "tag_only_visible_phrases")) {
    jiptWindow._jipt.push(["tag_only_visible_phrases", true]);
  }
  // Crowdin ignores this above 5000 source strings, which we are past; harmless, and it applies if we ever split the project.
  if (!jiptWindow._jipt.some((entry) => entry[0] === "preload_texts")) {
    jiptWindow._jipt.push(["preload_texts", true]);
  }
  if (options?.onEscape && !jiptWindow._jipt.some((entry) => entry[0] === "escape")) {
    jiptWindow._jipt.push(["escape", options.onEscape]);
  }

  const existingScript = document.getElementById(CROWDIN_JIPT_SCRIPT_ID) as HTMLScriptElement | null;
  if (existingScript) {
    if (options?.onFailure) {
      watchCrowdinInContextFailure(options.onFailure);
    }
    startAfterRender(options?.onFailure);
    return;
  }

  const script = document.createElement("script");
  script.id = CROWDIN_JIPT_SCRIPT_ID;
  script.type = "text/javascript";
  script.src = CROWDIN_JIPT_SRC;
  script.addEventListener("error", () => triggerCrowdinFailureExit(options?.onFailure), { once: true });
  script.addEventListener(
    "load",
    () => {
      if (options?.onFailure) {
        watchCrowdinInContextFailure(options.onFailure);
      }
      startAfterRender(options?.onFailure);
    },
    { once: true }
  );
  document.head.appendChild(script);
};

// Full teardown: stops JIPT, removes the injected script, clears its config
export const unloadCrowdinInContext = (): void => {
  stopCrowdinInContext();
  stopCrowdinInContextFailureWatch();

  const jiptWindow = window as unknown as JiptWindow;
  // Safety stop, in case JIPT is running even though our own state says it is not
  jiptWindow.jipt?.stop?.();
  document.getElementById(CROWDIN_JIPT_SCRIPT_ID)?.remove();
  jiptWindow._jipt = [];
  crowdinInContextRunning = false;
};
